// ShoppingList.js - Composant complet
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import './ShoppingList.css';
import ConfirmationModal from './ConfirmationModal';
import {
  calculateProgress,
  mapIngredientToCategory,
  mergeIngredients,
  mergeShoppingItems,
} from '../utils/shoppingUtils';
import useConfirmation from '../hooks/useConfirmation';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from './PageHeader';

function ShoppingList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  // États pour les données de la liste de courses
  const [shoppingItems, setShoppingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // États pour les filtres et options
  const [filterMode, setFilterMode] = useState('all'); // 'pending', 'all'
  const [dateRange, setDateRange] = useState('');
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // État pour l'ajout d'un nouvel article
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    quantity: '',
    unit: '',
  });

  // État pour l'édition d'un article existant
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // Cache de l'ID du document Firestore de la liste active (P1 — évite un getDocs par interaction)
  const activeListDocId = useRef(null);

  // Ref pour fermer le menu Actions au clic extérieur (U2)
  const actionsMenuRef = useRef(null);

  const { confirmationState, showConfirmation, handleConfirm, handleCancel } =
    useConfirmation();

  // Fonctions utilitaires

  // ✅ AJOUT : Nouvelles fonctions pour gérer les semaines
  // Obtenir le début de la semaine (lundi)
  const getStartOfWeek = (date = new Date()) => {
    const result = new Date(date);
    const day = result.getDay(); // 0 = dimanche, 1 = lundi, ..., 6 = samedi

    // Calculer la différence pour arriver au lundi
    // Si on est dimanche (0), on recule de 6 jours pour avoir le lundi précédent
    // Sinon, on recule de (day - 1) jours
    const diff = result.getDate() - day + (day === 0 ? -6 : 1);
    result.setDate(diff);
    result.setHours(0, 0, 0, 0);

    return result;
  };

  // Obtenir la fin de la semaine (dimanche)
  const getEndOfWeek = startOfWeek => {
    const result = new Date(startOfWeek);
    result.setDate(result.getDate() + 6);
    result.setHours(23, 59, 59, 999);
    return result;
  };

  // Détecter si on doit utiliser la semaine courante ou suivante
  const getRelevantWeek = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentDay = now.getDay(); // 0 = dimanche, 1 = lundi, etc.

    // Si on est vendredi après 18h, samedi ou dimanche, proposer la semaine suivante
    const shouldUseNextWeek =
      (currentDay === 5 && currentHour >= 18) || // Vendredi après 18h
      currentDay === 6 || // Samedi
      currentDay === 0; // Dimanche

    if (shouldUseNextWeek) {
      // Prendre la semaine suivante
      const nextWeek = new Date(now);
      nextWeek.setDate(now.getDate() + 7);
      return getStartOfWeek(nextWeek);
    } else {
      // Prendre la semaine courante
      return getStartOfWeek(now);
    }
  };

  // Améliorer le formatage des dates pour les semaines
  const formatWeekRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const startDay = start.getDate();
    const endDay = end.getDate();

    const months = [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre',
    ];

    const startMonth = months[start.getMonth()];
    const endMonth = months[end.getMonth()];
    const year = start.getFullYear();

    // Identifier le type de semaine
    const now = new Date();
    const currentWeekStart = getStartOfWeek(now);
    const isCurrentWeek = start.getTime() === currentWeekStart.getTime();

    const nextWeekStart = new Date(currentWeekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    const isNextWeek = start.getTime() === nextWeekStart.getTime();

    let weekLabel = '';
    if (isCurrentWeek) {
      weekLabel = 'Semaine courante ';
    } else if (isNextWeek) {
      weekLabel = 'Semaine prochaine ';
    } else {
      weekLabel = 'Semaine ';
    }

    // Formater les dates
    if (startMonth === endMonth) {
      return `${weekLabel}(${startDay}-${endDay} ${startMonth} ${year})`;
    } else {
      return `${weekLabel}(${startDay} ${startMonth} - ${endDay} ${endMonth} ${year})`;
    }
  };

  // Formater une date au format JJ/MM/YYYY
  const formatDate = date => {
    const d = new Date(date);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  // Formater une plage de dates pour l'affichage
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const startDay = start.getDate();
    const endDay = end.getDate();

    const months = [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre',
    ];

    const startMonth = months[start.getMonth()];
    const endMonth = months[end.getMonth()];

    if (startMonth === endMonth) {
      return `${startDay}-${endDay} ${startMonth} ${start.getFullYear()}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${start.getFullYear()}`;
    }
  };


  // Enregistrer la liste de courses dans Firestore
  const saveShoppingList = async (items, startDate, endDate) => {
    try {
      const listesCollection = collection(
        db,
        'listesCourses',
        auth.currentUser.uid,
        'listes'
      );

      if (activeListDocId.current) {
        // On connaît déjà l'ID — mise à jour directe sans getDocs
        await updateDoc(doc(listesCollection, activeListDocId.current), {
          articles: items,
          dateMiseAJour: serverTimestamp(),
          progression: calculateProgress(items),
        });
      } else {
        // Premier appel : vérifier s'il existe déjà une liste active
        const snapshot = await getDocs(
          query(listesCollection, where('dateCompletion', '==', null))
        );

        if (!snapshot.empty) {
          const listeDoc = snapshot.docs[0];
          activeListDocId.current = listeDoc.id;
          await updateDoc(doc(listesCollection, listeDoc.id), {
            articles: items,
            dateMiseAJour: serverTimestamp(),
            progression: calculateProgress(items),
          });
        } else {
          // Créer une nouvelle liste
          const newRef = await addDoc(listesCollection, {
            nom: `Liste du ${formatDate(startDate)} au ${formatDate(endDate)}`,
            dateDebut: startDate,
            dateFin: endDate,
            articles: items,
            dateCreation: serverTimestamp(),
            dateMiseAJour: serverTimestamp(),
            dateCompletion: null,
            progression: 0,
          });
          activeListDocId.current = newRef.id;
        }
      }
    } catch (err) {
      console.error("Erreur lors de l'enregistrement de la liste:", err);
      throw err;
    }
  };

  // Récupérer les ingrédients à partir des recettes (en parallèle)
  const getIngredientsFromRecipes = async (recipeIds, servingsMap) => {
    try {
      const snaps = await Promise.all(
        recipeIds.map(recipeId =>
          getDoc(doc(db, 'recettesUtilisateur', auth.currentUser.uid, 'recettes', recipeId))
        )
      );

      const ingredients = [];

      snaps.forEach((recipeSnap, index) => {
        if (!recipeSnap.exists()) return;
        const recipeId = recipeIds[index];
        const recipeData = recipeSnap.data();
        const factor = servingsMap[recipeId] || 1;

        if (recipeData.ingredients && recipeData.ingredients.length > 0) {
          recipeData.ingredients.forEach(ingredient => {
            const numericQty = parseFloat(ingredient.quantite);
            const quantity = ingredient.quantite
              ? (!isNaN(numericQty) ? numericQty * factor : ingredient.quantite)
              : null;

            ingredients.push({
              id: `${recipeId}_${ingredient.nom}`,
              name: ingredient.nom,
              quantity: quantity,
              unit: ingredient.unite || '',
              category: mapIngredientToCategory(ingredient.nom),
              checked: false,
              recipes: [recipeData.nom],
              recipeIds: [recipeId],
            });
          });
        }
      });

      return mergeIngredients(ingredients);
    } catch (err) {
      console.error('Erreur lors de la récupération des ingrédients:', err);
      throw err;
    }
  };

  // Charger la liste de courses la plus récente
  const loadLatestShoppingList = async (updateState = true) => {
    try {
      const listesCollection = collection(
        db,
        'listesCourses',
        auth.currentUser.uid,
        'listes'
      );

      const snapshot = await getDocs(
        query(listesCollection, where('dateCompletion', '==', null))
      );

      // S'il existe une liste active
      if (!snapshot.empty) {
        const listeDoc = snapshot.docs[0];
        const listeData = listeDoc.data();
        activeListDocId.current = listeDoc.id;

        if (updateState) {
          setDateRange(
            formatDateRange(
              listeData.dateDebut.toDate(),
              listeData.dateFin.toDate()
            )
          );
          setShoppingItems(listeData.articles || []);
        }

        return listeData.articles || [];
      } else {
        // Aucune liste active trouvée
        if (updateState) {
          // ✅ MODIF : Utiliser une vraie semaine au lieu de "aujourd'hui + 7 jours"
          const weekStart = getRelevantWeek();
          const weekEnd = getEndOfWeek(weekStart);

          setDateRange(formatWeekRange(weekStart, weekEnd));
          setShoppingItems([]);
        }

        return [];
      }
    } catch (err) {
      console.error('Erreur lors du chargement de la liste active:', err);
      throw err;
    }
  };

  // Charger la liste de courses à partir du planning
  const loadFromPlanning = async planningDates => {
    try {

      const { startDate, endDate } = planningDates;

      // Formater la plage de dates pour l'affichage
      setDateRange(formatDateRange(new Date(startDate), new Date(endDate)));

      // Récupérer les plans de repas pour la période
      const planningCollection = collection(
        db,
        'planningRepas',
        auth.currentUser.uid,
        'plans'
      );

      const q = query(
        planningCollection,
        where('date', '>=', new Date(startDate)),
        where('date', '<=', new Date(endDate))
      );

      const planningSnapshot = await getDocs(q);

      const recipeIds = new Set();
      const recipesServings = {};

      // Collecter tous les IDs de recettes et leur nombre de portions
      planningSnapshot.forEach(planDoc => {
        const planData = planDoc.data();
        if (planData.repas) {
          Object.values(planData.repas).forEach(meal => {
            if (meal && meal.idRecette) {
              recipeIds.add(meal.idRecette);
              // Compter le nombre de fois que cette recette est planifiée dans la semaine
              recipesServings[meal.idRecette] =
                (recipesServings[meal.idRecette] || 0) + 1;
            }
          });
        }
      });

      // Si aucune recette trouvée dans le planning
      if (recipeIds.size === 0) {
        const existingItems = await loadLatestShoppingList(false);
        if (existingItems && existingItems.length > 0) {
          setShoppingItems(existingItems);
          return;
        } else {
          setShoppingItems([]);
          return;
        }
      }

      // Récupérer les ingrédients des recettes du planning
      const newPlanningItems = await getIngredientsFromRecipes(
        Array.from(recipeIds),
        recipesServings
      );

      // ✅ CORRECTION PRINCIPALE : Récupérer la liste existante pour fusion
      const existingItems = await loadLatestShoppingList(false);

      let finalItems;
      if (existingItems && existingItems.length > 0) {
        finalItems = mergeShoppingItems(existingItems, newPlanningItems);
      } else {
        finalItems = newPlanningItems;
      }

      // Créer ou mettre à jour la liste de courses dans Firestore
      await saveShoppingList(
        finalItems,
        new Date(startDate),
        new Date(endDate)
      );
      setShoppingItems(finalItems);
    } catch (err) {
      console.error(
        '❌ Erreur lors du chargement des recettes du planning:',
        err
      );
      throw err;
    }
  };

  // Charger la liste de courses à partir d'une recette
  const loadFromRecipe = async recipeData => {
    try {
      // Si c'est un tableau, on a plusieurs recettes
      const recipes = Array.isArray(recipeData) ? recipeData : [recipeData];

      const recipeIds = recipes.map(r => r.recipeId);
      const recipesServings = {};

      recipes.forEach(r => {
        recipesServings[r.recipeId] = r.servings || 1;
      });

      // Récupérer les détails des recettes
      const recipeItems = await getIngredientsFromRecipes(
        recipeIds,
        recipesServings
      );

      // Récupérer la liste actuelle pour fusionner les articles
      const currentItems = await loadLatestShoppingList(false);

      // Fusionner les articles
      const mergedItems = mergeShoppingItems(currentItems, recipeItems);

      // Enregistrer la liste mise à jour
      // ✅ MODIF : Utiliser une vraie semaine
      const weekStart = getRelevantWeek();
      const weekEnd = getEndOfWeek(weekStart);

      await saveShoppingList(mergedItems, weekStart, weekEnd);

      setDateRange(formatWeekRange(weekStart, weekEnd));
      setShoppingItems(mergedItems);
    } catch (err) {
      console.error('Erreur lors du chargement de la recette:', err);
      throw err;
    }
  };

  // Articles triés : non cochés A-Z en premier, cochés A-Z en bas
  const sortedItems = (() => {
    const byName = (a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
    const unchecked = shoppingItems.filter(i => !i.checked).sort(byName);
    const checked   = shoppingItems.filter(i =>  i.checked).sort(byName);
    const all = [...unchecked, ...checked];
    return filterMode === 'pending' ? unchecked : all;
  })();

  // Gestion des interactions utilisateur

  // Mettre à jour le statut d'un article (coché/décoché)
  const toggleItemChecked = async itemId => {
    const updatedItems = shoppingItems.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );
    setShoppingItems(updatedItems);

    try {
      if (!activeListDocId.current) return;
      const listesCollection = collection(db, 'listesCourses', auth.currentUser.uid, 'listes');
      await updateDoc(doc(listesCollection, activeListDocId.current), {
        articles: updatedItems,
        dateMiseAJour: serverTimestamp(),
        progression: calculateProgress(updatedItems),
      });
    } catch (err) {
      console.error("Erreur lors de la mise à jour de l'article:", err);
      setShoppingItems(shoppingItems);
    }
  };

  // Supprimer un article spécifique de la liste
  const deleteShoppingItem = async (itemId, event) => {
    event.stopPropagation();

    // Trouver l'article à supprimer pour afficher ses détails
    const itemToDelete = shoppingItems.find(item => item.id === itemId);
    if (!itemToDelete) return;

    // Préparer les détails de confirmation
    const itemDetails = (
      <div>
        <strong>Article à supprimer :</strong>
        <br />• {itemToDelete.name}
        {itemToDelete.quantity && (
          <span>
            {' '}
            ({itemToDelete.quantity} {itemToDelete.unit})
          </span>
        )}
        {itemToDelete.recipes && itemToDelete.recipes.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <strong>Utilisé dans :</strong> {itemToDelete.recipes.join(', ')}
          </div>
        )}
      </div>
    );

    showConfirmation({
      title: 'Supprimer cet article',
      message:
        'Cet article sera définitivement retiré de votre liste de courses.',
      details: itemDetails,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger',
      onConfirm: async () => {
        try {
          const updatedItems = shoppingItems.filter(item => item.id !== itemId);
          setShoppingItems(updatedItems);

          if (activeListDocId.current) {
            const listesCollection = collection(db, 'listesCourses', auth.currentUser.uid, 'listes');
            await updateDoc(doc(listesCollection, activeListDocId.current), {
              articles: updatedItems,
              dateMiseAJour: serverTimestamp(),
              progression: calculateProgress(updatedItems),
            });
          }

          setMessage({
            type: 'success',
            text: `"${itemToDelete.name}" retiré de la liste`,
          });
          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (err) {
          console.error("Erreur lors de la suppression de l'article:", err);
          setError("Une erreur s'est produite lors de la suppression de l'article.");
        }
      },
    });
  };


  // Ajouter un nouvel article
  const handleAddItem = async () => {
    if (!newItem.name.trim()) return;

    try {
      const newId = Date.now().toString();
      const newShoppingItem = {
        id: newId,
        name: newItem.name.trim(),
        quantity: newItem.quantity || null,
        unit: newItem.unit || '',
        category: 'Autres',
        checked: false,
        recipes: [],
        recipeIds: [],
      };

      const updatedItems = [...shoppingItems, newShoppingItem];
      setShoppingItems(updatedItems);

      const listesCollection = collection(db, 'listesCourses', auth.currentUser.uid, 'listes');

      if (activeListDocId.current) {
        await updateDoc(doc(listesCollection, activeListDocId.current), {
          articles: updatedItems,
          dateMiseAJour: serverTimestamp(),
        });
      } else {
        const weekStart = getRelevantWeek();
        const weekEnd = getEndOfWeek(weekStart);
        const newRef = await addDoc(listesCollection, {
          nom: `Liste ${formatWeekRange(weekStart, weekEnd)}`,
          dateDebut: weekStart,
          dateFin: weekEnd,
          articles: updatedItems,
          dateCreation: serverTimestamp(),
          dateMiseAJour: serverTimestamp(),
          dateCompletion: null,
          progression: 0,
        });
        activeListDocId.current = newRef.id;
        setDateRange(formatWeekRange(weekStart, weekEnd));
      }

      setNewItem({ name: '', quantity: '', unit: '' });
      setShowAddItemForm(false);
    } catch (err) {
      console.error("Erreur lors de l'ajout de l'article:", err);
    }
  };



  // --- Édition d'un article existant (U1) ---

  const startEditItem = (item, e) => {
    e.stopPropagation();
    setEditingItemId(item.id);
    setEditingItem({ ...item });
  };

  const handleSaveEdit = async () => {
    if (!editingItem.name.trim()) return;

    const updatedItems = shoppingItems.map(item =>
      item.id === editingItemId ? { ...editingItem, name: editingItem.name.trim() } : item
    );
    setShoppingItems(updatedItems);
    setEditingItemId(null);
    setEditingItem(null);

    try {
      if (!activeListDocId.current) return;
      const listesCollection = collection(db, 'listesCourses', auth.currentUser.uid, 'listes');
      await updateDoc(doc(listesCollection, activeListDocId.current), {
        articles: updatedItems,
        dateMiseAJour: serverTimestamp(),
      });
    } catch (err) {
      console.error("Erreur lors de la modification de l'article:", err);
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingItem(null);
  };

  // --- Fermeture menu Actions au clic extérieur (U2) ---

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadFromSelectedRecipes = async locationState => {
    try {

      const { selectedRecipes, dateRange } = locationState;
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);

      setDateRange(formatDateRange(startDate, endDate));

      const recipeIds = Object.keys(selectedRecipes);
      const recipesServings = selectedRecipes;

      if (recipeIds.length === 0) {
        setShoppingItems([]);
        return;
      }

      // Récupérer les ingrédients des recettes
      const newRecipeItems = await getIngredientsFromRecipes(
        recipeIds,
        recipesServings
      );

      // Vérifier s'il existe déjà une liste active pour fusion
      const existingItems = await loadLatestShoppingList(false);

      let finalItems;
      if (existingItems && existingItems.length > 0) {
        finalItems = mergeShoppingItems(existingItems, newRecipeItems);
      } else {
        finalItems = newRecipeItems;
      }

      // Sauvegarder la liste finale
      await saveShoppingList(finalItems, startDate, endDate);
      setShoppingItems(finalItems);
    } catch (err) {
      console.error('❌ Erreur lors du chargement depuis les recettes:', err);
      throw err;
    }
  };
  // Chargement initial des données
  useEffect(() => {
    const loadShoppingList = async () => {
      if (!auth.currentUser) {
        navigate('/');
        return;
      }

      try {
        setLoading(true);
        setError('');

        // ÉTAPE 1: Vérifier d'abord le state de navigation
        const locationState = location.state;

        if (locationState?.fromRecipes && locationState?.selectedRecipes) {
          await loadFromSelectedRecipes(locationState);
        } else if (sessionStorage.getItem('planningDates')) {
          const fromPlanning = sessionStorage.getItem('planningDates');
          await loadFromPlanning(JSON.parse(fromPlanning));
          sessionStorage.removeItem('planningDates');
        } else if (sessionStorage.getItem('addToShoppingList')) {
          const fromRecipe = sessionStorage.getItem('addToShoppingList');
          await loadFromRecipe(JSON.parse(fromRecipe));
          sessionStorage.removeItem('addToShoppingList');
        } else {
          const existingItems = await loadLatestShoppingList(true);

          if (!existingItems || existingItems.length === 0) {
            const today = new Date();
            const endDate = new Date();
            endDate.setDate(today.getDate() + 7);

            setDateRange(formatDateRange(today, endDate));
            setShoppingItems([]);
            await saveShoppingList([], today, endDate);
          }
        }
      } catch (err) {
        console.error('❌ Erreur lors du chargement:', err);
        setError(
          'Erreur lors du chargement de la liste de courses. Veuillez réessayer.'
        );
      } finally {
        setLoading(false);
      }
    };

    loadShoppingList();
  }, [navigate, location]); // eslint-disable-line react-hooks/exhaustive-deps


  // Calculs pour l'affichage
  const progressPercentage = calculateProgress(shoppingItems);
  const completedItems = shoppingItems.filter(item => item.checked).length;

  if (loading) {
    return <div className="loading">Chargement de la liste de courses...</div>;
  }

  return (
    <div className={`shopping-list-container ${theme}`}>
      {/* ENTÊTE FIXE */}
      <PageHeader
        title="Ma Liste de Courses"
        backTo="/"
        backAriaLabel="Retour à l'accueil"
        sticky={true}
      >
        {/* Barre de progression */}
        <div className="progress-section">
          <div className="progress-dates">
            <span>Semaine du {dateRange}</span>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          <div className="progress-info">
            <span>
              {completedItems} sur {shoppingItems.length} articles
            </span>
            <span>{progressPercentage}% complété</span>
          </div>
        </div>

        {/* Filtres et Options */}
        <div className="shopping-list-actions">
          <div className="filter-buttons">
            <button
              className={`filter-button ${
                filterMode === 'all' ? 'active' : ''
              }`}
              onClick={() => setFilterMode('all')}
            >
              Tous
            </button>
            <button
              className={`filter-button ${
                filterMode === 'pending' ? 'active' : ''
              }`}
              onClick={() => setFilterMode('pending')}
            >
              Restants
            </button>
          </div>

          <div className="option-buttons">
            <div className="actions-dropdown" ref={actionsMenuRef}>
              <button
                className="actions-button"
                onClick={() => setShowActionsMenu(!showActionsMenu)}
              >
                Actions
              </button>
              {showActionsMenu && (
                <div className="actions-menu">
                  {/* Action vider la liste */}
                  <div
                    className="menu-action danger"
                    onClick={() => {
                      const totalItems = shoppingItems.length;
                      const checkedItems = shoppingItems.filter(
                        item => item.checked
                      ).length;
                      const uncheckedItems = totalItems - checkedItems;

                      if (totalItems === 0) {
                        setError('Votre liste de courses est déjà vide.');
                        setShowActionsMenu(false);
                        return;
                      }

                      // Préparer les détails de la liste
                      const listDetails = (
                        <div>
                          <strong>Contenu de la liste :</strong>
                          <br />• {totalItems} article
                          {totalItems > 1 ? 's' : ''} au total
                          <br />• {checkedItems} article
                          {checkedItems > 1 ? 's' : ''} coché
                          {checkedItems > 1 ? 's' : ''}
                          <br />• {uncheckedItems} article
                          {uncheckedItems > 1 ? 's' : ''} restant
                          {uncheckedItems > 1 ? 's' : ''}
                          {uncheckedItems > 0 && (
                            <div
                              style={{
                                marginTop: '10px',
                                padding: '8px',
                                backgroundColor: '#fff3e0',
                                borderRadius: '4px',
                                fontSize: '13px',
                              }}
                            >
                              ⚠️ Attention : {uncheckedItems} article
                              {uncheckedItems > 1 ? 's' : ''} non coché
                              {uncheckedItems > 1 ? 's' : ''}{' '}
                              {uncheckedItems > 1 ? 'seront' : 'sera'} également
                              supprimé{uncheckedItems > 1 ? 's' : ''}.
                            </div>
                          )}
                        </div>
                      );

                      showConfirmation({
                        title: 'Vider toute la liste',
                        message:
                          'Cette action supprimera définitivement tous les articles de votre liste de courses.',
                        details: listDetails,
                        confirmText: 'Vider la liste',
                        cancelText: 'Annuler',
                        type: 'danger',
                        onConfirm: async () => {
                          try {
                            setShoppingItems([]);

                            // Mettre à jour dans Firestore
                            const today = new Date();
                            const endDate = new Date();
                            endDate.setDate(today.getDate() + 7);
                            await saveShoppingList([], today, endDate);

                            // Message de succès
                            setMessage({
                              type: 'success',
                              text: `Liste vidée avec succès (${totalItems} article${
                                totalItems > 1 ? 's' : ''
                              } supprimé${totalItems > 1 ? 's' : ''})`,
                            });
                            setTimeout(
                              () => setMessage({ type: '', text: '' }),
                              4000
                            );
                          } catch (err) {
                            console.error(
                              'Erreur lors du vidage de la liste:',
                              err
                            );
                            setError(
                              'Erreur lors du vidage de la liste. Veuillez réessayer.'
                            );
                          }
                        },
                      });

                      setShowActionsMenu(false);
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                    Vider la liste
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </PageHeader>

      {/* ZONE SCROLLABLE */}
      <div className="shopping-list-scrollable">
        {error && <div className="error-message">{error}</div>}
        {message.text && (
          <div className={`shopping-message shopping-message-${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Liste de courses — plate, triée A-Z (cochés en bas) */}
        <div className="shopping-list">
          {sortedItems.length > 0 ? (
            <ul className="items-list">
              {sortedItems.map(item => (
                <li
                  key={item.id}
                  className={`shopping-item ${item.checked ? 'checked' : ''}`}
                >
                  {editingItemId === item.id ? (
                    /* Mode édition */
                    <div className="item-edit-form">
                      <input
                        className="item-edit-input item-edit-name"
                        type="text"
                        value={editingItem.name}
                        onChange={e => setEditingItem({ ...editingItem, name: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                        autoFocus
                      />
                      <input
                        className="item-edit-input item-edit-qty"
                        type="number"
                        placeholder="Qté"
                        value={editingItem.quantity || ''}
                        onChange={e => setEditingItem({ ...editingItem, quantity: e.target.value })}
                      />
                      <input
                        className="item-edit-input item-edit-unit"
                        type="text"
                        placeholder="Unité"
                        value={editingItem.unit || ''}
                        onChange={e => setEditingItem({ ...editingItem, unit: e.target.value })}
                      />
                      <div className="item-edit-actions">
                        <button className="item-edit-save" onClick={handleSaveEdit}>✓</button>
                        <button className="item-edit-cancel" onClick={handleCancelEdit}>✕</button>
                      </div>
                    </div>
                  ) : (
                    /* Mode affichage */
                    <div className="item-container">
                      <div className="item-checkbox" onClick={() => toggleItemChecked(item.id)}>
                        {item.checked
                          ? <span className="checkbox checked">✓</span>
                          : <span className="checkbox"></span>}
                      </div>

                      <div className="item-content" onClick={() => toggleItemChecked(item.id)}>
                        <span className="item-text">
                          {item.name}
                          {item.quantity && (
                            <span className="item-quantity">
                              {' '}({item.quantity}{item.unit ? ` ${item.unit}` : ''})
                            </span>
                          )}
                        </span>
                      </div>

                      <button
                        className="edit-item-button"
                        onClick={e => startEditItem(item, e)}
                        title="Modifier l'article"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>

                      <button
                        className="delete-item-button"
                        onClick={e => deleteShoppingItem(item.id, e)}
                        title="Supprimer l'article"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="empty-list-message">
              <p>Votre liste de courses est vide.</p>
              <p>
                Ajoutez des articles manuellement ci-dessous, ou{' '}
                <button className="inline-text-link" onClick={() => navigate('/meal-planner')}>
                  planifiez vos repas
                </button>
                {' '}pour générer automatiquement votre liste.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* BOUTON D'AJOUT FIXE */}
      <div className="shopping-list-fixed-footer">
        {!showAddItemForm ? (
          <button
            className="add-item-button"
            onClick={() => setShowAddItemForm(true)}
          >
            + Ajouter un article
          </button>
        ) : (
          <div className="add-item-form">
            <h3>Ajouter un article</h3>
            <div className="form-row">
              <input
                type="text"
                placeholder="Nom de l'article"
                value={newItem.name}
                onChange={e => setNewItem({ ...newItem, name: e.target.value })}
              />
            </div>
            <div className="form-row">
              <input
                type="number"
                placeholder="Quantité"
                value={newItem.quantity}
                onChange={e =>
                  setNewItem({ ...newItem, quantity: e.target.value })
                }
              />
              <input
                type="text"
                placeholder="Unité"
                value={newItem.unit}
                onChange={e => setNewItem({ ...newItem, unit: e.target.value })}
              />
            </div>
            <div className="form-actions">
              <button
                className="cancel-button"
                onClick={() => setShowAddItemForm(false)}
              >
                Annuler
              </button>
              <button className="save-button" onClick={handleAddItem}>
                Ajouter
              </button>
            </div>
          </div>
        )}
      </div>

      {/* NOUVEAU: Modal de confirmation */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        details={confirmationState.details}
      />
    </div>
  );
}

export default ShoppingList;
